import { css } from "@emotion/css";
import { BeaconInline, ProgressTrackerItem, type ProgressTrackerItemState } from "@octopusdeploy/design-system-components";
import { borderRadius, fontWeight, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import React, { useEffect, useState } from "react";
import type { ActionEvent } from "~/analytics/Analytics";
import { useAnalyticActionDispatch } from "~/analytics/Analytics";
import InternalLink from "~/components/Navigation/InternalLink";
import { useIsPortalUrlActive } from "~/components/Navigation/useIsPortalUrlActive";
import { ProjectStatusPopover } from "./ProjectStatusPopover";
interface ProjectStatusItemProps {
    name: string;
    href?: LinkHref;
    state: ProgressTrackerItemState;
    showIndicator?: boolean;
    popover?: ProjectStatusPopover;
    eventName?: string;
    eventAction?: ActionEvent;
    onClick?: () => void;
}
export const ProjectStatusItem = ({ name, state, href, showIndicator: isInteractive, popover, eventName, eventAction, onClick }: ProjectStatusItemProps): JSX.Element => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);
    const [showPopover, setShowPopover] = useState<boolean>(false);
    const ref = React.createRef<HTMLDivElement>();
    useEffect(() => {
        setAnchorEl(ref.current);
    }, [ref]);
    const onMouseEnter = () => {
        setShowPopover(true);
    };
    const onMouseLeave = () => {
        setShowPopover(false);
    };
    const dispatchAction = useAnalyticActionDispatch();
    const isUrlActive = useIsPortalUrlActive();
    const handleClick = () => {
        if (onClick) {
            onClick();
        }
        if (eventName && eventAction && href && !isUrlActive(href, true)) {
            dispatchAction(eventName, eventAction);
        }
    };
    return (<div ref={ref} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            {state === "Current" && isInteractive && popover && href ? (<>
                    <InternalLink to={href} className={styles.container} onClick={handleClick}>
                        <BeaconInline size={"small"} ripple={0.5} open={true}/>
                        <span>{name}</span>
                    </InternalLink>
                    <ProjectStatusPopover popoverType={popover?.popoverType} unassociatedTargetRoles={popover?.unassociatedTargetRoles} onCustomAction={popover?.onCustomAction} open={showPopover} anchorEl={anchorEl} eventAction={eventAction}/>
                </>) : (<ProgressTrackerItem label={name} state={state} onClick={handleClick} href={href}/>)}
        </div>);
};
const styles = {
    container: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "start",
        gap: space[8],
        borderRadius: borderRadius.small,
        whiteSpace: "nowrap",
        padding: `${space[4]} ${space[8]}`,
        border: "none",
        font: "inherit",
        textDecoration: "none",
        background: themeTokens.color.background.primary.default,
        fontWeight: fontWeight[500],
        cursor: "pointer",
        color: themeTokens.color.text.primary,
        "&&:hover": {
            color: themeTokens.color.text.primary,
            background: themeTokens.color.background.primary.pressed,
        },
    }),
};
